import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { FiCheckCircle } from 'react-icons/fi';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`Added ${quantity}x ${product.name} to cart`);
  };

  if (loading) {
    return (
      <div className="py-24 text-center" data-testid="loading-indicator">
        <p className="text-text-muted">Loading product...</p>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Product Image */}
          <div className="bg-background-subtle rounded-lg p-12 flex items-center justify-center">
            <img
              src={product.product_image || "https://images.unsplash.com/photo-1589807502991-d64e6459096c?crop=entropy&cs=srgb&fm=jpg&q=85"}
              alt={product.name}
              className="max-w-full h-auto rounded-lg"
            />
          </div>

          {/* Product Info */}
          <div>
            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-accent-subtle text-accent-main text-sm font-medium rounded mb-4">
                {product.category}
              </span>
              <h1 className="text-4xl font-heading font-bold tracking-tighter text-primary-main mb-2" data-testid="product-title">
                {product.name}
              </h1>
              <p className="text-text-muted font-mono text-sm">{product.concentration}</p>
              <p className="text-text-muted font-mono text-sm">SKU: {product.sku}</p>
            </div>

            <div className="mb-8">
              <div className="text-3xl font-mono font-bold text-primary-main mb-2" data-testid="product-price">
                ${product.price} AUD
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 text-sm font-mono">
                  Purity: {product.purity}
                </span>
                {product.in_stock ? (
                  <span className="inline-block px-3 py-1 bg-success/10 text-success text-sm font-medium rounded" data-testid="stock-status">
                    In Stock ({product.stock_count} available)
                  </span>
                ) : (
                  <span className="inline-block px-3 py-1 bg-error/10 text-error text-sm font-medium rounded" data-testid="stock-status">
                    Out of Stock
                  </span>
                )}
              </div>
            </div>

            {/* Overview */}
            <div className="mb-8">
              <h3 className="text-xl font-heading font-semibold text-primary-main mb-3">Overview</h3>
              <p className="text-text-secondary leading-relaxed">{product.description}</p>
            </div>

            {/* Specifications */}
            <div className="mb-8">
              <h3 className="text-xl font-heading font-semibold text-primary-main mb-3">Specifications</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-text-secondary">Purity</span>
                  <span className="font-mono text-text-primary">{product.purity}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-text-secondary">Form</span>
                  <span className="text-text-primary">Lyophilised powder</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-text-secondary">Storage</span>
                  <span className="text-text-primary">Refrigeration recommended</span>
                </div>
                {product.batch_number && (
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-text-secondary">Batch Number</span>
                    <span className="font-mono text-text-primary">{product.batch_number}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quality Assurance */}
            <div className="mb-8">
              <h3 className="text-xl font-heading font-semibold text-primary-main mb-3">Quality Assurance</h3>
              <p className="text-text-secondary text-sm mb-4">
                This batch has been independently tested with a Certificate of Analysis available.
              </p>
              {product.coa_image && (
                <img
                  src={product.coa_image}
                  alt="Certificate of Analysis"
                  className="border border-slate-200 rounded-lg max-w-xs cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => window.open(product.coa_image, '_blank')}
                  data-testid="coa-image"
                />
              )}
            </div>

            {/* Add to Cart */}
            <div className="border-t border-slate-200 pt-8">
              {product.in_stock ? (
                <>
                  <div className="flex items-center gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Quantity</label>
                      <input
                        type="number"
                        min="1"
                        max={product.stock_count}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock_count, parseInt(e.target.value) || 1)))}
                        className="w-24 border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                        data-testid="quantity-input"
                      />
                      <p className="text-xs text-text-muted mt-1">Max: {product.stock_count}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleAddToCart}
                    className="btn-primary w-full"
                    data-testid="add-to-cart-button"
                  >
                    Add to Cart
                  </button>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-error font-medium mb-2">This product is currently out of stock</p>
                  <p className="text-sm text-text-secondary">Please check back later or contact us for availability</p>
                </div>
              )}
            </div>

            {/* Shipping Info */}
            <div className="mt-8 bg-background-subtle rounded-lg p-6">
              <h4 className="font-heading font-semibold text-primary-main mb-3 flex items-center gap-2">
                <FiCheckCircle className="text-success" />
                Shipping Information
              </h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li>• Australian domestic shipping</li>
                <li>• Discreet packaging</li>
                <li>• Tracking included</li>
              </ul>
            </div>

            {/* Disclaimer */}
            <div className="mt-6 bg-slate-100 border border-slate-200 rounded-lg p-6">
              <p className="text-xs text-text-secondary font-medium">
                <strong className="text-text-primary">DISCLAIMER:</strong> For laboratory research purposes only. Not for human or veterinary use. Not intended to diagnose, treat, cure, or prevent any disease.
              </p>
            </div>
          </div>
        </div>

        {/* Customer Reviews Section */}
        {reviews.length > 0 && (
          <div className="mt-24">
            <h2 className="text-3xl font-heading font-bold text-primary-main mb-8">Customer Reviews</h2>
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.id} className="bg-white border border-slate-200 rounded-lg p-6" data-testid={`review-${review.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-heading font-semibold text-primary-main">{review.customer_name}</h4>
                        {review.verified_purchase && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded">
                            <FiCheckCircle className="w-3 h-3" />
                            Verified Purchase
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className={`w-5 h-5 ${i < review.rating ? 'text-warning fill-current' : 'text-slate-300'}`}
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <p className="text-text-secondary leading-relaxed">{review.comment}</p>
                  <p className="text-xs text-text-muted mt-3">
                    {new Date(review.created_at).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
